import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSBlogAuthor } from '../../../services';

interface AuthorFormData {
  nome: string;
  cargo: string;
  bio: string;
  avatar: string;
  email: string;
  linkedin: string;
  twitter: string;
  status: 'ativo' | 'inativo';
}

const initialFormData: AuthorFormData = {
  nome: '',
  cargo: '',
  bio: '',
  avatar: '',
  email: '',
  linkedin: '',
  twitter: '',
  status: 'ativo',
};

const BlogAuthors: React.FC = () => {
  const { t } = useTranslation();
  const [authors, setAuthors] = useState<CMSBlogAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<CMSBlogAuthor | null>(null);
  const [formData, setFormData] = useState<AuthorFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; author: CMSBlogAuthor | null }>({
    open: false,
    author: null,
  });

  const fetchAuthors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cmsService.getBlogAuthors();
      setAuthors(response.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingAuthors'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  const handleOpenDialog = (author?: CMSBlogAuthor) => {
    if (author) {
      setEditingAuthor(author);
      setFormData({
        nome: author.nome,
        cargo: author.cargo || '',
        bio: author.bio || '',
        avatar: author.avatar || '',
        email: author.email || '',
        linkedin: author.linkedin || '',
        twitter: author.twitter || '',
        status: author.status,
      });
    } else {
      setEditingAuthor(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAuthor(null);
    setFormData(initialFormData);
  };

  const handleChange = (field: keyof AuthorFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingAuthor) {
        await cmsService.updateBlogAuthor(editingAuthor.id, formData);
        setSuccess(t('cms.authorUpdated'));
      } else {
        await cmsService.createBlogAuthor(formData);
        setSuccess(t('cms.authorCreated'));
      }
      handleCloseDialog();
      fetchAuthors();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingAuthor'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.author) return;
    try {
      await cmsService.deleteBlogAuthor(deleteDialog.author.id);
      setSuccess(t('cms.authorDeleted'));
      setDeleteDialog({ open: false, author: null });
      fetchAuthors();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingAuthor'));
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.blogAuthors')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.blogAuthorsSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('cms.newAuthor')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.author')}</TableCell>
                <TableCell>{t('cms.contact')}</TableCell>
                <TableCell>{t('cms.networks')}</TableCell>
                <TableCell>{t('cms.articlesCount')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : authors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noAuthorFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                authors.map((author) => (
                  <TableRow key={author.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar src={author.avatar}>
                          {getInitials(author.nome)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {author.nome}
                          </Typography>
                          {author.cargo && (
                            <Typography variant="caption" color="text.secondary">
                              {author.cargo}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {author.email ? (
                        <Typography variant="body2" color="text.secondary">
                          {author.email}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          {t('cms.noEmail')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {author.linkedin && (
                          <Tooltip title="LinkedIn">
                            <IconButton
                              size="small"
                              href={author.linkedin}
                              target="_blank"
                            >
                              <LinkedInIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {author.twitter && (
                          <Tooltip title="Twitter">
                            <IconButton
                              size="small"
                              href={author.twitter}
                              target="_blank"
                            >
                              <TwitterIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!author.linkedin && !author.twitter && (
                          <Typography variant="caption" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={author.total_artigos || 0}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={author.status === 'ativo' ? 'success' : 'default'}
                        label={author.status === 'ativo' ? t('common.active') : t('common.inactive')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(author)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, author })}
                          disabled={(author.total_artigos || 0) > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAuthor ? t('cms.editAuthor') : t('cms.newAuthor')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.nome}
            onChange={handleChange('nome')}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('cms.position')}
            value={formData.cargo}
            onChange={handleChange('cargo')}
            margin="normal"
            placeholder={t('cms.positionPlaceholderAuthor')}
          />
          <TextField
            fullWidth
            label={t('cms.bio')}
            value={formData.bio}
            onChange={handleChange('bio')}
            margin="normal"
            multiline
            rows={3}
            placeholder={t('cms.bioPlaceholder')}
          />
          <TextField
            fullWidth
            label={t('cms.avatarUrl')}
            value={formData.avatar}
            onChange={handleChange('avatar')}
            margin="normal"
            placeholder="https://..."
          />
          <TextField
            fullWidth
            label={t('common.email')}
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="LinkedIn"
            value={formData.linkedin}
            onChange={handleChange('linkedin')}
            margin="normal"
            placeholder="https://linkedin.com/in/..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkedInIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Twitter"
            value={formData.twitter}
            onChange={handleChange('twitter')}
            margin="normal"
            placeholder="https://twitter.com/..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TwitterIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            label={t('common.status')}
            value={formData.status}
            onChange={handleChange('status')}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="ativo">{t('common.active')}</option>
            <option value="inativo">{t('common.inactive')}</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.nome}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, author: null })}
      >
        <DialogTitle>{t('cms.deleteAuthor')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteAuthor', { name: deleteDialog.author?.nome })}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('cms.actionIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, author: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogAuthors;
